import { Callout, SearchInput } from "@octopusdeploy/design-system-components";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { DeploymentTargetResource, ProjectResource, Repository, GetLiveStatusResponse, KubernetesLiveStatusResource, TimelineItem, EnvironmentV2Resource, ArgoCDInstanceSummaryResource } from "@octopusdeploy/octopus-server-client";
import { HostingEnvironment, TaskState, Permission } from "@octopusdeploy/octopus-server-client";
import { isEqual } from "lodash";
import { useEffect, useMemo, useState } from "react";
import type { LiveInformationSource } from "~/analytics/AnalyticSession";
import { LiveInformationSourceTypes } from "~/analytics/AnalyticSession";
import type { ViewLiveStatusPageEvent } from "~/analytics/Analytics";
import { DeploymentTimelineOpenedTriggerPage, useAnalyticDeploymentTimelineOpenedDispatch, useAnalyticLiveStatusPageDispatch } from "~/analytics/Analytics";
import type { LiveObjectStatusFilter } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { filterArgoCDInstanceResources, LiveStatusFiltering, defaultFilter, filterMachineResources } from "~/areas/projects/components/Observability/LiveStatusFiltering";
import { LiveStatusTable } from "~/areas/projects/components/Observability/LiveStatusTable";
import { LiveStatusObjectDrawer } from "~/areas/projects/components/Observability/ObjectDrawer/LiveStatusObjectDrawer";
import { DeploymentTimelineDrawer } from "~/areas/projects/components/Releases/Deployments/DeploymentTimelineDrawer";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import { LiveStatusSummaryBar } from "./LiveStatusSummaryBar";
export interface LoaderData {
    latestDeployment: TimelineItem;
    environment: EnvironmentV2Resource;
    kubernetesLiveStatus: GetLiveStatusResponse;
    deploymentTargets: DeploymentTargetResource[];
    argoCDInstances: ArgoCDInstanceSummaryResource[];
    isRunningInHACluster: boolean;
}
type SharedLiveStatusPageProps = {
    loaderData: LoaderData;
    project: ProjectResource;
    tenantId?: string;
};
const refetchIntervalInMs = 1000;
const ExcludedTaskStates: string[] = [TaskState.Queued];
const getLiveInformationSources = (kubernetesLiveStatus: GetLiveStatusResponse): LiveInformationSource[] => {
    const hasMonitorStatuses = kubernetesLiveStatus.MachineStatuses.length > 0;
    const hasArgoStatuses = (kubernetesLiveStatus.ArgoCDInstanceStatuses ?? []).length > 0;
    const sources: LiveInformationSource[] = [];
    if (hasMonitorStatuses) {
        sources.push(LiveInformationSourceTypes.KubernetesMonitor);
    }
    if (hasArgoStatuses) {
        sources.push(LiveInformationSourceTypes.ArgoCD);
    }
    return sources;
};
export function SharedLiveStatusPage({ loaderData, project, tenantId }: SharedLiveStatusPageProps) {
    const [filter, setFilter] = useState<LiveObjectStatusFilter>(defaultFilter);
    const [resourceInDrawer, setResourceInDrawer] = useState<KubernetesLiveStatusResource | undefined>(undefined);
    const [isTimelineDrawerOpen, setIsTimelineDrawerOpen] = useState<boolean>(false);
    const [timelineOpenEventTracked, setTimelineOpenEventTracked] = useState<boolean>(false);
    const isGrpcHaEnabled = useOctopusFeatureToggle("grpc-high-availability", false);
    const { result: { latestDeployment, kubernetesLiveStatus, deploymentTargets, argoCDInstances, isRunningInHACluster }, } = useQuery(async (repository) => refresh(repository, project.Id, loaderData.environment.Id, tenantId), [project.Id, loaderData.environment.Id, tenantId], "Live Status Page", {
        initialResult: loaderData,
        refetchIntervalInMs,
    });
    const disabledBecauseHaIsNotSupported = isRunningInHACluster && !isGrpcHaEnabled;
    const liveStatusSummary = kubernetesLiveStatus.Summary;
    const machineResourcesToRender = filterMachineResources(kubernetesLiveStatus.MachineStatuses, filter);
    const argoCDInstanceResourcesToRender = filterArgoCDInstanceResources(kubernetesLiveStatus?.ArgoCDInstanceStatuses ?? [], filter);
    function updateFilterContent<K extends keyof LiveObjectStatusFilter>(updates: Pick<LiveObjectStatusFilter, K>) {
        setFilter({ ...defaultFilter, ...updates });
    }
    const trackTimelineOpened = useAnalyticDeploymentTimelineOpenedDispatch();
    const showTimelineDrawer = () => {
        if (!timelineOpenEventTracked) {
            trackTimelineOpened({
                deploymentId: loaderData.latestDeployment.Id,
                releaseId: loaderData.latestDeployment.ReleaseId,
                environmentId: loaderData.latestDeployment.EnvironmentId,
                projectId: loaderData.latestDeployment.ProjectId,
                health: liveStatusSummary.Status,
                trigger: DeploymentTimelineOpenedTriggerPage.LiveStatusPage,
            });
            setTimelineOpenEventTracked(true);
        }
        setIsTimelineDrawerOpen(true);
    };
    const additionalKindTypes = useMemo(() => [...new Set(kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Kind)).concat((kubernetesLiveStatus?.ArgoCDInstanceStatuses ?? []).flatMap((m) => m.Resources.map((r) => r.Kind))))], [kubernetesLiveStatus.MachineStatuses, kubernetesLiveStatus.ArgoCDInstanceStatuses]);
    const namespaceNames: string[] = useMemo(() => [
        ...new Set(
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        kubernetesLiveStatus.MachineStatuses.flatMap((m) => m.Resources.map((r) => r.Namespace as string))
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            .concat((kubernetesLiveStatus?.ArgoCDInstanceStatuses ?? []).flatMap((m) => m.Resources.map((r) => r.Namespace as string)))
            .filter((ns) => !!ns)),
    ], [kubernetesLiveStatus.MachineStatuses, kubernetesLiveStatus.ArgoCDInstanceStatuses]);
    const filters = disabledBecauseHaIsNotSupported
        ? undefined
        : {
            inputs: [<SearchInput autoFocus={true} value={filter.name} onChange={(x) => updateFilterContent({ name: x })} accessibleName={"search by object name"} placeholder={"Search by object name..."}/>],
            advancedFilters: {
                onResetFilter: () => setFilter(defaultFilter),
                hasUserSelectedValues: !isEqual(defaultFilter, filter),
                content: <LiveStatusFiltering filter={filter} setFilter={setFilter} additionalKindTypes={additionalKindTypes} namespaceNames={namespaceNames}/>,
            },
        };
    return (<>
            <PageContent header={{
            title: `${project.Name} ${loaderData.environment.Name} Live Status`,
            pageActions: isAllowed({ permission: Permission.InterruptionView, project: project.Id, environment: "*", tenant: "*" })
                ? [{ type: "button", buttonType: "secondary", label: "Timeline", onClick: () => showTimelineDrawer() }]
                : undefined,
        }} filters={filters} description={!disabledBecauseHaIsNotSupported && <LiveStatusSummaryBar spaceId={project.SpaceId} projectSlug={project.Slug} liveStatusSummary={liveStatusSummary} latestDeployment={latestDeployment}/>}>
                {disabledBecauseHaIsNotSupported && <Callout type={"warning"} title={"Kubernetes Monitor is not supported in HA clusters"}/>}
                {!disabledBecauseHaIsNotSupported && (<LiveStatusTable spaceId={project.SpaceId} deploymentTargets={deploymentTargets} argoCDInstances={argoCDInstances} machineLiveStatusResources={machineResourcesToRender} argoCDInstanceLiveStatusResources={argoCDInstanceResourcesToRender} resourceSelected={(resource) => setResourceInDrawer(resource)}/>)}
            </PageContent>
            <LiveStatusObjectDrawer environmentId={loaderData.environment.Id} projectId={project.Id} tenantId={tenantId} onClose={() => setResourceInDrawer(undefined)} resource={resourceInDrawer} latestDeployment={latestDeployment} analyticEventData={{
            releaseId: latestDeployment.ReleaseId,
            deploymentStatus: latestDeployment.TaskState,
            liveApplicationHealth: liveStatusSummary.Status,
            executionType: "deployment",
        }}/>
            <DeploymentTimelineDrawer isOpen={isTimelineDrawerOpen} onClose={() => setIsTimelineDrawerOpen(false)} deploymentName={project.Name} environmentId={loaderData.environment.Id} projectId={project.Id} spaceId={project.SpaceId} tenantId={tenantId} referringReleaseId={loaderData.latestDeployment.ReleaseId}/>
            <LiveStatusPageAnalyticView eventData={{
            projectId: project.Id,
            releaseId: latestDeployment.ReleaseId,
            environmentId: loaderData.environment.Id,
            deploymentId: latestDeployment.Id,
            deploymentStatus: latestDeployment.TaskState,
            liveApplicationHealth: liveStatusSummary.Status,
            liveInformationSources: getLiveInformationSources(loaderData.kubernetesLiveStatus),
        }}/>
        </>);
}
export async function sharedLiveStatusPageLoader(repository: Repository, environmentId: string, projectContext: Promise<ProjectContextProps>, tenantId?: string): Promise<LoaderData> {
    const { state: { model: project }, } = await projectContext;
    const refreshingData = await refresh(repository, project.Id, environmentId, tenantId);
    return {
        ...refreshingData,
        environment: await repository.Environments.getByIdV2({ spaceId: project.SpaceId, id: environmentId }),
    };
}
async function refresh(repository: Repository, projectId: string, environmentId: string, tenantId?: string) {
    // There was more complicated logic ensuring that tenantId was null before, I can't reproduce the issue so I'm leaving this here to ensure that I haven't missed an edge case
    const nullableTenantId: string | null = tenantId === undefined ? null : tenantId;
    const latestDeploymentPromise = getLatestTimelineItem(repository, projectId, environmentId, nullableTenantId);
    const kubernetesLiveStatusPromise = repository.KubernetesLiveStatus.getLiveStatus(projectId, environmentId, tenantId);
    const serverNodesPromise = repository.OctopusServerNodes.all();
    const isSelfHosted = (await repository.Licenses.getCurrentStatus()).HostingEnvironment === HostingEnvironment.SelfHosted;
    const kubernetesLiveStatus = await kubernetesLiveStatusPromise;
    const argoCDInstances = await repository.ArgoCDInstances.list();
    return {
        latestDeployment: await latestDeploymentPromise,
        kubernetesLiveStatus,
        deploymentTargets: await repository.Machines.all({ ids: kubernetesLiveStatus.MachineStatuses.map((r) => r.MachineId) }),
        argoCDInstances: argoCDInstances.Resources,
        isRunningInHACluster: isSelfHosted && (await serverNodesPromise).length > 1,
    };
}
async function getLatestTimelineItem(repository: Repository, projectId: string, environmentId: string, tenantId: string | null) {
    // We only need the latest timeline item that is not in the ServerTaskState.Queued
    // The BFF handler already does the sorting for us so we can just request one item
    const deploymentsTimeline = await repository.Deployments.getDeploymentTimeline(projectId, environmentId, tenantId || undefined, 0, 1, ExcludedTaskStates);
    return deploymentsTimeline.TimelineItems.Items[0];
}
function LiveStatusPageAnalyticView({ eventData }: {
    eventData: ViewLiveStatusPageEvent;
}) {
    const dispatch = useAnalyticLiveStatusPageDispatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => dispatch("View Live Status Page", eventData), []);
    return <></>;
}
